import { useEffect, useMemo, useRef } from 'react'

import styles from './ParticleLayer.module.scss'
import type { ParticleLayerProps } from './ParticleLayer.types'

export const ParticleLayer = ({
  layer,
  assetBaseUrl,
  ready,
  onLoaded,
  onFailed
}: ParticleLayerProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const particles = useMemo(() => {
    let seed = layer.seed
    const random = () => {
      seed = (seed * 16807) % 2147483647
      return (seed - 1) / 2147483646
    }
    const sample = ([min, max]: [number, number]) =>
      min + random() * (max - min)
    return Array.from({ length: layer.count }, () => ({
      x: sample(layer.emitter.x),
      y: sample(layer.emitter.y),
      dx: sample(layer.travel.x),
      dy: sample(layer.travel.y),
      size: sample(layer.sizePx),
      opacity: sample(layer.opacityRange),
      sway: sample(layer.sway) * (random() > 0.5 ? 1 : -1),
      spin: sample(layer.spin),
      rotation: random() * 360,
      duration: sample(layer.durationMs),
      phase: random(),
      source:
        layer.appearance.kind === 'image'
          ? layer.appearance.sources[
              Math.floor(random() * layer.appearance.sources.length)
            ]
          : undefined
    }))
  }, [layer])

  useEffect(() => {
    if (layer.appearance.kind === 'dot') {
      onLoaded(layer.id)
      return
    }
    let cancelled = false
    // Decode each unique sprite once before starting any motion.
    const images = layer.appearance.sources.map(source => {
      const image = new Image()
      image.src = `${assetBaseUrl}${source}`
      return image
    })
    void Promise.all(images.map(image => image.decode()))
      .then(() => {
        if (!cancelled) onLoaded(layer.id)
      })
      .catch(() => {
        if (!cancelled) onFailed()
      })
    return () => {
      cancelled = true
    }
  }, [layer, assetBaseUrl, onLoaded, onFailed])

  useEffect(() => {
    const root = ref.current
    if (!root || !ready) return
    if (typeof ResizeObserver === 'undefined') {
      onFailed()
      return
    }
    let animations: Animation[] = []
    let previousWidth = 0
    let previousHeight = 0
    const animate = () => {
      const width = root.clientWidth
      const height = root.clientHeight
      if (width === previousWidth && height === previousHeight) return
      previousWidth = width
      previousHeight = height
      const times = animations.map(animation => animation.currentTime)
      animations.forEach(animation => animation.cancel())
      try {
        animations = []
        particles.forEach((particle, index) => {
          const element = root.children[index]
          const animation = element.animate(
            [0, 0.15, 0.35, 0.6, 0.85, 1].map(t => ({
              offset: t,
              transform: `translate(${((particle.x + particle.dx * t + particle.sway * Math.sin(t * Math.PI * 2)) * width) / 100}px, ${((particle.y + particle.dy * t) * height) / 100}px) rotate(${particle.rotation + particle.spin * t}deg) rotateY(${Math.sin(t * Math.PI * 4) * layer.flutterDeg}deg)`,
              opacity: t === 0 || t === 1 ? 0 : particle.opacity
            })),
            {
              duration: particle.duration,
              delay: -particle.phase * particle.duration,
              iterations: Infinity,
              easing: 'linear',
              fill: 'both'
            }
          )
          const currentTime = times[index]
          if (typeof currentTime === 'number')
            animation.currentTime = currentTime
          animations.push(animation)
        })
      } catch {
        onFailed()
      }
    }
    animate()
    const observer = new ResizeObserver(animate)
    observer.observe(root)
    return () => {
      observer.disconnect()
      animations.forEach(animation => animation.cancel())
    }
  }, [ready, particles, layer.flutterDeg, onFailed])

  const size = (value: typeof layer.layout.width) =>
    value.maxPixels
      ? `min(${value.value}${value.unit}, ${value.maxPixels}px)`
      : `${value.value}${value.unit}`
  const appearance = layer.appearance
  return (
    <div
      ref={ref}
      className={styles.layer}
      data-layer={layer.id}
      style={{
        left: `${layer.layout.left}%`,
        top: `${layer.layout.top}%`,
        width: size(layer.layout.width),
        height: layer.layout.height ? size(layer.layout.height) : undefined,
        aspectRatio: layer.layout.aspectRatio,
        opacity: layer.opacity
      }}
    >
      {particles.map((particle, index) => (
        <div
          key={index}
          className={styles.particle}
          style={{
            width: particle.size,
            height: particle.size,
            background:
              appearance.kind === 'dot'
                ? appearance.soft
                  ? `radial-gradient(circle, ${appearance.color} 0%, transparent 70%)`
                  : appearance.color
                : undefined,
            borderRadius: appearance.kind === 'dot' ? '50%' : undefined
          }}
        >
          {particle.source && (
            <img
              src={`${assetBaseUrl}${particle.source}`}
              alt=''
              decoding='async'
            />
          )}
        </div>
      ))}
    </div>
  )
}
